import React, {useState} from "react";
import {useForm} from "react-hook-form";
import Api from "../../Api/api";
import {toast} from "react-toastify";
import {GrClose} from "react-icons/gr";
import {useStateValue} from "../../states/StateProvider";
import {ScaleLoader} from "react-spinners";

const AddUsers = () => {
    const [loading, setLoading] = useState(false)
    const [{addUsersModal, addUsersDone}, dispatch] = useStateValue();
    const {
        register, getValues, setValue, watch, handleSubmit, formState, reset, formState: {errors, touchedFields},
        control
    } = useForm({mode: "onChange"});
    const {isValid} = formState;

    const onSubmit = async (data) => {
        setLoading(true)

        // Handle Mailservice checkbox - set both IC and IM admins
        if (data.isMailservice) {
            data.icAdmin = true;
            data.imAdmin = true;
        } else {
            data.icAdmin = false;
            data.imAdmin = false;
        }
        delete data.isMailservice;

        Api().post('/registerByAdmin', data).then(res => {
            if (res.status === 201) {
                setLoading(false)
                dispatch({type: "SET_ADDUSERS_MODAL", item: !addUsersModal})
                dispatch({type: "SET_ADDUSERS_DONE", item: !addUsersDone})
                reset() // Reset form after successful submission
                toast.success('Benutzer erfolgreich hinzugefügt und E-Mail gesendet')
            }
        }).catch(e => {
            setLoading(false)
            if (e.response?.status === 503) {
                toast.error(e.response.data)
            } else {
                toast.error('Etwas ist schief gelaufen!')
            }
        })
    };

    const closeModal = () => {
        dispatch({type: "SET_ADDUSERS_MODAL", item: false})
    }

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-text bg-opacity-50">
            <div className="relative w-full max-w-2xl bg-white p-6 rounded-lg shadow-lg mx-4 max-h-[90vh] overflow-y-auto">
                <GrClose
                    className='cursor-pointer absolute top-4 right-4 hover:opacity-70'
                    onClick={closeModal}
                    size='24px'
                />

                <h2 className='text-xl font-bold text-center mb-2'>Neuen Benutzer Hinzufügen</h2>
                <p className='text-xs text-grey text-center mb-4'>* Pflichtfeld</p>

                <form onSubmit={handleSubmit(onSubmit)} className='px-6 py-1'>
                    <section className='flex flex-col text-left text-grey text-sm mb-4'>
                        <label className='py-2 font-medium'>E-Mail *</label>
                        <input
                            placeholder='E-Mail'
                            {...register('email', {
                                required: 'E-Mail ist erforderlich',
                                pattern: {
                                    value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                                    message: 'Bitte geben Sie eine gültige E-Mail ein',
                                },
                            })}
                            type="email"
                            required
                            className='w-full p-2 border rounded focus:border-mainBlue focus:outline-none'
                            style={{border: errors.email && '1px solid red'}}
                        />
                        {errors.email && touchedFields.email && <p className='text-red-500 text-xs mt-1'>{errors.email.message}</p>}
                    </section>

                    <section className='flex flex-col text-left text-grey text-sm mb-4'>
                        <label className='py-2 font-medium'>Passwort *</label>
                        <input
                            placeholder='Passwort'
                            type='text'
                            {...register('password', {
                                required: 'Ihr Passwort ist erforderlich',
                                pattern: {
                                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!"#&'()*+,\-./:;<=>?@\[\\\]^_`{|}~$])[a-zA-Z\d!"#&'()*+,\-./:;<=>?@\[\\\]^_`{|}~$]{8,}$/,
                                    message: 'Mindestens 8 Zeichen, bestehend aus Groß-, Kleinbuchstaben, Zahlen, Sonderzeichen - ohne: § %',
                                },
                            })}
                            required
                            className='w-full p-2 border rounded focus:border-mainBlue focus:outline-none'
                            style={{border: errors.password ? '1px solid red' : ''}}
                        />
                        {errors.password && touchedFields.password && <p className='text-red-500 text-xs mt-1'>{errors.password.message}</p>}
                    </section>

                    <section className='flex flex-col text-left text-grey text-sm mb-4'>
                        <label className='py-2 font-medium'>Passwort wiederholen *</label>
                        <input
                            placeholder='Passwort wiederholen'
                            type='text'
                            {...register("confirm_password", {
                                required: 'Ihr Passwort ist erforderlich',
                                validate: (val) => {
                                    if (watch('password') !== val) {
                                        return "Ihre Passwörter stimmen nicht überein";
                                    }
                                }
                            })}
                            required
                            className='w-full p-2 border rounded focus:border-mainBlue focus:outline-none'
                            style={{border: errors.confirm_password && '1px solid red'}}
                        />
                        {errors.confirm_password && touchedFields.confirm_password && <p className='text-red-500 text-xs mt-1'>{errors.confirm_password.message}</p>}
                    </section>

                    <div className='grid grid-cols-2 gap-4 mb-4'>
                        <section className='flex flex-col text-left text-grey text-sm'>
                            <label className='py-2 font-medium'>Vorname</label>
                            <input
                                placeholder='Vorname'
                                type='text'
                                {...register('vorname', {
                                    pattern: {
                                        value: /^.{1,50}$/,
                                        message: 'maximal 50 Zeichen erlaubt',
                                    },
                                })}
                                className='w-full p-2 border rounded focus:border-mainBlue focus:outline-none'
                                style={{border: errors.vorname && '1px solid red'}}
                            />
                            {errors.vorname && touchedFields.vorname && <p className='text-red-500 text-xs mt-1'>{errors.vorname.message}</p>}
                        </section>

                        <section className='flex flex-col text-left text-grey text-sm'>
                            <label className='py-2 font-medium'>Nachname</label>
                            <input
                                placeholder='Nachname'
                                type='text'
                                {...register('nachname', {
                                    pattern: {
                                        value: /^.{1,50}$/,
                                        message: 'maximal 50 Zeichen erlaubt',
                                    },
                                })}
                                className='w-full p-2 border rounded focus:border-mainBlue focus:outline-none'
                                style={{border: errors.nachname && '1px solid red'}}
                            />
                            {errors.nachname && touchedFields.nachname && <p className='text-red-500 text-xs mt-1'>{errors.nachname.message}</p>}
                        </section>
                    </div>

                    <section className='flex flex-col text-left text-grey text-sm mb-4'>
                        <label className='py-2 font-medium'>VP-Nummer *</label>
                        <input
                            placeholder='VP-Nummer'
                            type='text'
                            {...register('partnernr', {
                                required: 'Ihr VP-Nummer ist erforderlich',
                                pattern: {
                                    value: /^.{1,20}$/,
                                    message: 'maximal 20 Zeichen erlaubt',
                                },
                            })}
                            required
                            className='w-full p-2 border rounded focus:border-mainBlue focus:outline-none'
                            style={{border: errors.partnernr && '1px solid red'}}
                        />
                        {errors.partnernr && touchedFields.partnernr && <p className='text-red-500 text-xs mt-1'>{errors.partnernr.message}</p>}
                    </section>

                    <section className='mb-4'>
                        <label htmlFor="field-aktiv" className='flex items-center cursor-pointer'>
                            <input
                                {...register("aktiv")}
                                type="checkbox"
                                id="field-aktiv"
                                className='mr-2'
                            />
                            <span className='text-sm text-grey'>Aktiv</span>
                        </label>
                    </section>

                    <section className='mb-4'>
                        <h3 className='text-md font-bold text-left mb-2'>Rolle *</h3>
                        <div className='space-y-3'>
                            <div className='flex gap-x-6'>
                                <label htmlFor="field-role-4" className='flex items-center cursor-pointer w-32'>
                                    <input
                                        {...register("role")}
                                        type="radio"
                                        value="4"
                                        id="field-role-4"
                                        className='mr-2'
                                    />
                                    <span className='text-sm'>Management</span>
                                </label>
                                <label htmlFor="field-role-5" className='flex items-center cursor-pointer w-32'>
                                    <input
                                        {...register("role")}
                                        type="radio"
                                        value="5"
                                        id="field-role-5"
                                        className='mr-2'
                                    />
                                    <span className='text-sm'>Struktur</span>
                                </label>
                                <label htmlFor="field-role-6" className='flex items-center cursor-pointer w-32'>
                                    <input
                                        {...register("role")}
                                        type="radio"
                                        value="6"
                                        id="field-role-6"
                                        className='mr-2'
                                    />
                                    <span className='text-sm'>Controlling</span>
                                </label>
                            </div>
                            <div className='flex gap-x-6 items-start'>
                                <label htmlFor="field-role-2" className='flex items-center cursor-pointer w-32'>
                                    <input
                                        {...register("role")}
                                        type="radio"
                                        defaultChecked
                                        value="2"
                                        id="field-role-2"
                                        className='mr-2'
                                    />
                                    <span className='text-sm'>FKB</span>
                                </label>
                                <label htmlFor="field-role-3" className='flex items-center cursor-pointer w-32'>
                                    <input
                                        {...register("role")}
                                        type="radio"
                                        value="3"
                                        id="field-role-3"
                                        className='mr-2'
                                    />
                                    <span className='text-sm'>VP</span>
                                </label>
                                <div className='w-50'>
                                    <label htmlFor="field-role-1" className='flex items-center cursor-pointer mb-3'>
                                        <input
                                            {...register("role")}
                                            type="radio"
                                            value="1"
                                            id="field-role-1"
                                            className='mr-2'
                                        />
                                        <span className='text-sm'>Innendienst</span>
                                    </label>
                                    {watch('role') === '1' && (
                                        <div className='ml-6 space-y-2'>
                                            <label htmlFor="field-isSAdmin" className='flex items-center cursor-pointer'>
                                                <input
                                                    type="checkbox"
                                                    {...register("isSAdmin")}
                                                    id="field-isSAdmin"
                                                    className='mr-2'
                                                />
                                                <span className='text-sm'>SuperAdmin</span>
                                            </label>
                                            <label htmlFor="field-admin" className='flex items-center cursor-pointer'>
                                                <input
                                                    type="checkbox"
                                                    {...register("admin")}
                                                    id="field-admin"
                                                    className='mr-2'
                                                />
                                                <span className='text-sm'>Benutzerverwaltung</span>
                                            </label>
                                            <label htmlFor="field-isMailservice" className='flex items-center cursor-pointer'>
                                                <input
                                                    type="checkbox"
                                                    {...register("isMailservice")}
                                                    id="field-isMailservice"
                                                    className='mr-2'
                                                />
                                                <span className='text-sm'>Mailservice (IC+IM)</span>
                                            </label>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className='flex gap-4 mt-6'>
                        <button
                            type="submit"
                            disabled={!isValid || loading}
                            className={`flex-1 py-2 px-4 rounded text-white transition-colors ${
                                (isValid && !loading) ? 'bg-mainBlue hover:bg-opacity-90 cursor-pointer' : 'bg-disableBlue opacity-50 cursor-not-allowed'
                            }`}
                        >
                            {loading ? (
                                <span className='flex items-center justify-center gap-2'>
                                    <ScaleLoader height={12} color="#ffffff" />
                                    <span>Speichere...</span>
                                </span>
                            ) : (
                                'Benutzer speichern'
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={closeModal}
                            className='flex-1 py-2 px-4 rounded text-white bg-cancel'
                        >
                            Abbrechen
                        </button>
                    </section>
                </form>
            </div>
        </div>
    )
}

export default AddUsers
